"use client";

import { useState } from "react";
import FaqAccordionItem from "@/app/components/faq/FaqAccordionItem";

export type ContactFaqItem = {
  question: string;
  answer: string;
  order?: number;
  isVisible?: boolean;
};

interface ContactFaqSectionProps {
  title?: string;
  items: ContactFaqItem[];
}

/**
 * Contact page FAQ accordion - matches main FAQ page styling.
 */
export default function ContactFaqSection({
  title = "Frequently Asked Questions",
  items,
}: ContactFaqSectionProps) {
  const visible = items
    .filter((item) => item.isVisible !== false && item.question.trim())
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const [openIds, setOpenIds] = useState<Set<number>>(() => new Set());

  if (!visible.length) return null;

  const toggle = (id: number) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section
      className="mt-40 mb-16 max-w-4xl md:mx-auto mx-5"
      aria-label={title}
    >
      <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-8 sm:mb-10">
        {title}
      </h2>
      <div className="space-y-3 sm:space-y-4">
        {visible.map((faq, index) => {
          const isOpen = openIds.has(index);
          const faqId = `contact-faq-${index}`;
          const answerId = `contact-faq-answer-${index}`;

          return (
            <FaqAccordionItem
              key={`${faq.question}-${index}`}
              id={faqId}
              isOpen={isOpen}
              onToggle={() => toggle(index)}
              question={faq.question}
            >
              <div id={answerId}>
                <p className="text-sm sm:text-base text-[#F5F5F5]/80 leading-relaxed whitespace-pre-wrap">
                  {faq.answer}
                </p>
              </div>
            </FaqAccordionItem>
          );
        })}
      </div>
    </section>
  );
}
